import React, { useState, useEffect } from 'react';
import moment from 'moment';
import MainLayout from '../../components/Layout/MainLayout';
import { PageCard, Badge, BtnSecondary, EmptyState, Skeleton } from '../../components/UI/UIComponents';
import './History.css';

const API_BASE   = process.env.REACT_APP_API_URL    || `http://localhost:${process.env.REACT_APP_PORT || 5001}`;
const CLIENT_URL = localStorage.getItem('clientUrl') || process.env.REACT_APP_CLIENT_URL || `http://localhost:${process.env.REACT_APP_CLIENT_PORT || 3006}`;

const SearchIcon  = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{width:16,height:16}}><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>;
const DownloadIcon= () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{width:15,height:15}}><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>;
const RefreshIcon = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{width:15,height:15}}><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 11-2.12-9.36L23 10"/></svg>;
const LogIcon     = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{width:15,height:15}}><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>;
const ZoomIcon    = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{width:13,height:13}}><path d="M15 10l5 2-5 2V10z"/><path d="M4 8h9a2 2 0 012 2v4a2 2 0 01-2 2H4a2 2 0 01-2-2v-4a2 2 0 012-2z"/></svg>;
const DeleteIcon  = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{width:14,height:14}}><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2"/></svg>;

const MEETING_TYPES = { 1:'Instant', 2:'Scheduled', 3:'Recurring', 8:'Recurring (Fixed)' };

const parseLogLine = (line) => {
  try { const o = JSON.parse(line); return { level: o.level || 'info', message: o.message || line, time: o.timestamp || '' }; }
  catch { return { level: 'info', message: line, time: '' }; }
};

const History = () => {
  const [emailInput,  setEmailInput]  = useState(localStorage.getItem('email') || '');
  const [meetings,    setMeetings]    = useState([]);
  const [loading,     setLoading]     = useState(false);
  const [error,       setError]       = useState('');
  const [debugInfo,   setDebugInfo]   = useState('');
  const [search,      setSearch]      = useState('');
  const [typeFilter,  setTypeFilter]  = useState('all');
  const [page,        setPage]        = useState(1);
  const [totalRecs,   setTotalRecs]   = useState(0);
  const [nextToken,   setNextToken]   = useState('');
  // ── Server logs ──
  const [logs,        setLogs]        = useState([]);
  const [logLoading,  setLogLoading]  = useState(false);
  const [logOpen,     setLogOpen]     = useState(false);

  const PER_PAGE = 10;

  // ── Fetch meetings using native fetch (no axios) ──
  // Fetches both upcoming (scheduled/recurring) AND past meetings
  const loadMeetings = async (email, appendToken = '') => {
    if (!email || !email.trim()) {
      setError('Please enter a host email and click Load Meetings.');
      return;
    }

    setLoading(true);
    setError('');

    const trimmed = encodeURIComponent(email.trim());

    // If paginating, only fetch the same type with the token
    if (appendToken) {
      const url = `${API_BASE}/meetings?email=${trimmed}&type=previous_meetings&page_size=50&next_page_token=${encodeURIComponent(appendToken)}`;
      setDebugInfo(`Calling: ${url}`);
      try {
        const res = await fetch(url);
        const text = await res.text();
        if (!res.ok) { setError(`Server error ${res.status}: ${text}`); return; }
        const data = JSON.parse(text);
        const incoming = data.meetings || [];
        setMeetings(prev => [...prev, ...incoming]);
        setTotalRecs(prev => prev + incoming.length);
        setNextToken(data.next_page_token || '');
        setDebugInfo(`✓ ${incoming.length} more meetings loaded`);
      } catch (err) {
        setError(`Network error: ${err.message}. Is the server running at ${API_BASE}?`);
      } finally { setLoading(false); }
      return;
    }

    // Fetch both upcoming AND past meetings in parallel
    const types = ['upcoming', 'previous_meetings'];
    const urls = types.map(t => `${API_BASE}/meetings?email=${trimmed}&type=${t}&page_size=50`);
    setDebugInfo(`Calling: ${urls.join(' + ')}`);
    console.log('[History] GET (parallel)', urls);

    try {
      const responses = await Promise.all(urls.map(u => fetch(u)));
      const results   = await Promise.all(responses.map(async (res, i) => {
        if (!res.ok) {
          console.warn(`[History] ${types[i]} returned ${res.status}`);
          return { meetings: [], total_records: 0, next_page_token: '' };
        }
        return JSON.parse(await res.text());
      }));

      // Merge & deduplicate by meeting id
      const allMeetings = [];
      const seen = new Set();
      for (const data of results) {
        for (const m of (data.meetings || [])) {
          if (!seen.has(m.id)) {
            seen.add(m.id);
            allMeetings.push(m);
          }
        }
      }

      // Sort: upcoming first (by date desc), then past
      allMeetings.sort((a, b) => {
        const da = a.date === '—' ? '' : a.date;
        const db = b.date === '—' ? '' : b.date;
        return db.localeCompare(da);
      });

      setMeetings(allMeetings);
      setTotalRecs(allMeetings.length);
      setNextToken(results[1]?.next_page_token || ''); // pagination for past meetings
      setDebugInfo(`✓ ${allMeetings.length} meetings loaded (${results[0]?.meetings?.length || 0} upcoming + ${results[1]?.meetings?.length || 0} past)`);
    } catch (err) {
      console.error('[History] Error:', err);
      setError(`Network error: ${err.message}. Is the server running at ${API_BASE}?`);
      setDebugInfo(`FAILED: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // ── Auto-load on mount ──
  useEffect(() => {
    // Try both keys in case Settings saved under either
    const saved = localStorage.getItem('email') || localStorage.getItem('hostEmail') || '';
    console.log('[History] Mount — email:', saved, 'API_BASE:', API_BASE);
    setDebugInfo(`API: ${API_BASE} | email key "email": "${localStorage.getItem('email')}" | "hostEmail": "${localStorage.getItem('hostEmail')}"`);
    if (saved) {
      setEmailInput(saved);
      loadMeetings(saved);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLoad = () => {
    const email = emailInput.trim();
    if (!email) { setError('Enter a host email first.'); return; }
    localStorage.setItem('email', email);
    localStorage.setItem('hostEmail', email); // save under both keys
    setPage(1);
    setMeetings([]);
    loadMeetings(email);
  };

  // ── Fetch server logs ──
  const fetchLogs = async () => {
    setLogLoading(true);
    try {
      const res = await fetch(`${API_BASE}/serverlogs`);
      const text = await res.text();
      setLogs(text.split('\n').filter(Boolean).map(parseLogLine).reverse());
    } catch (err) {
      setLogs([{ level: 'warn', message: `Could not fetch server logs: ${err.message}`, time: '' }]);
    } finally {
      setLogLoading(false);
    }
  };

  const openLogs = () => { setLogOpen(true); fetchLogs(); };

  // ── Filter ──
  const filtered = meetings.filter(m => {
    const q = search.toLowerCase();
    return (!q || m.name?.toLowerCase().includes(q))
        && (typeFilter === 'all' || String(m.type) === typeFilter);
  });
  const totalPages = Math.ceil(filtered.length / PER_PAGE);
  const paginated  = filtered.slice((page-1)*PER_PAGE, page*PER_PAGE);

  const [deleting, setDeleting] = useState(null); // meeting id being deleted

  const deleteMeeting = async (meetingId, topic) => {
    if (!window.confirm(`Delete meeting "${topic || meetingId}"?\nThis will permanently remove it from Zoom.`)) return;
    setDeleting(meetingId);
    try {
      const res = await fetch(`${API_BASE}/meetings/${meetingId}`, { method: 'DELETE' });
      if (!res.ok) {
        const text = await res.text();
        alert(`Failed to delete: ${text}`);
        return;
      }
      setMeetings(prev => prev.filter(m => m.id !== meetingId));
      setTotalRecs(prev => Math.max(0, prev - 1));
    } catch (err) {
      alert(`Delete failed: ${err.message}`);
    } finally {
      setDeleting(null);
    }
  };

  const exportCSV = () => {
    const rows = meetings.map(m =>
      `"${m.name}","${m.host}","${m.date}","${m.time}","${m.duration}","${MEETING_TYPES[m.type]||m.type}","${m.joinUrl}"`
    );
    const blob = new Blob(['Topic,Host,Date,Time,Duration,Type,Join URL\n' + rows.join('\n')], { type:'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `zoom-meetings-${moment().format('YYYY-MM-DD')}.csv`;
    a.click();
  };

  return (
    <MainLayout pageTitle="Meeting History" pageSubtitle="All meetings from your Zoom account">
      <div className="history-page">

        {/* ── Email bar ── */}
        <div className="hist-email-bar">
          <span className="hist-email-label">Host Email</span>
          <input
            className="hist-email-input"
            type="email"
            placeholder="e.g. user@gmail.com"
            value={emailInput}
            onChange={e => setEmailInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleLoad()}
          />
          <button className="hist-email-btn" onClick={handleLoad} disabled={loading}>
            {loading ? 'Loading…' : <><RefreshIcon/>&nbsp;Load Meetings</>}
          </button>
        </div>

        {/* ── Debug bar ── */}
        {debugInfo && (
          <div className="hist-debug-bar">
            <strong>Debug:</strong> {debugInfo}
          </div>
        )}

        {/* ── Error ── */}
        {error && <div className="hist-error-bar">⚠ {error}</div>}

        {/* ── Stats ── */}
        <div className="hist-stats">
          {[
            { label:'Total',     value: loading ? '…' : totalRecs },
            { label:'Scheduled', value: meetings.filter(m=>m.type===2).length },
            { label:'Instant',   value: meetings.filter(m=>m.type===1).length },
            { label:'Recurring', value: meetings.filter(m=>m.type===3||m.type===8).length },
          ].map((s,i) => (
            <div className="hist-stat-pill" key={i}>
              <span className="hist-stat-val">{s.value}</span>
              <span className="hist-stat-lbl">{s.label}</span>
            </div>
          ))}
        </div>

        {/* ── Main table card ── */}
        <PageCard
          title="Zoom Meetings"
          subtitle={loading ? 'Loading from Zoom…' : `${filtered.length} of ${totalRecs} meetings`}
          action={
            <div style={{display:'flex', gap:8, flexWrap:'wrap'}}>
              <BtnSecondary onClick={exportCSV} disabled={!meetings.length}><DownloadIcon/> CSV</BtnSecondary>
              <BtnSecondary onClick={openLogs}><LogIcon/> Server Logs</BtnSecondary>
              <BtnSecondary onClick={() => { setPage(1); setMeetings([]); loadMeetings(emailInput); }}><RefreshIcon/> Refresh</BtnSecondary>
            </div>
          }
        >
          {/* Filters */}
          <div className="hist-filters">
            <div className="form-input-wrap has-icon" style={{flex:1, maxWidth:300}}>
              <span className="form-input-icon"><SearchIcon/></span>
              <input className="form-input" type="text" placeholder="Search topic…" value={search}
                onChange={e => { setSearch(e.target.value); setPage(1); }}/>
            </div>
            <div className="hist-filter-tabs">
              {[{k:'all',l:'All'},{k:'2',l:'Scheduled'},{k:'1',l:'Instant'},{k:'3',l:'Recurring'}].map(f=>(
                <button key={f.k} className={`hist-tab${typeFilter===f.k?' active':''}`}
                  onClick={()=>{ setTypeFilter(f.k); setPage(1); }}>{f.l}</button>
              ))}
            </div>
          </div>

          {/* Table */}
          {loading && meetings.length === 0 ? (
            <div style={{padding:'20px 0'}}>
              {[1,2,3,4,5].map(i => <div key={i} style={{marginBottom:10}}><Skeleton height={18} radius={4}/></div>)}
            </div>
          ) : paginated.length === 0 ? (
            <EmptyState icon="📋"
              title={emailInput ? 'No meetings found' : 'Enter host email above'}
              description={emailInput ? 'Try a different filter or click Refresh.' : 'Type your Zoom host email and click Load Meetings.'}
            />
          ) : (
            <div className="hist-table-wrap">
              <table className="hist-table">
                <thead>
                  <tr><th>Topic</th><th>Meeting ID</th><th>Date & Time</th><th>Duration</th><th>Type</th><th>Status</th><th>Join</th><th>Action</th></tr>
                </thead>
                <tbody>
                  {paginated.map(m => (
                    <tr key={m.uuid || m.id}>
                      <td><span className="hist-meeting-name">{m.name || '(Untitled)'}</span></td>
                      <td><span className="hist-meeting-id">{m.id}</span></td>
                      <td>
                        <span className="hist-date">{m.date}</span>
                        {m.time !== '—' && <><br/><span className="hist-time">{m.time}{m.timezone ? ` (${m.timezone})` : ''}</span></>}
                      </td>
                      <td>{m.duration}</td>
                      <td><Badge label={MEETING_TYPES[m.type] || `Type ${m.type}`} variant="info"/></td>
                      <td><Badge label={m.status || 'completed'} variant={m.status === 'started' ? 'orange' : 'success'}/></td>
                      <td>
                        {m.joinUrl
                          ? <a className="hist-join-link" href="#join" onClick={(e) => {
                              e.preventDefault();
                              const userName = localStorage.getItem('displayName') || 'Rukunujjaman Miaji';
                              const lang = localStorage.getItem('defaultLang') || 'en-US';
                              const clientUrl = m.joinUrl.replace(
                                /https?:\/\/[a-z0-9.]*zoom\.us\/j\//,
                                `${CLIENT_URL}?`
                              ) + `?role=1?name=${encodeURIComponent(userName)}?lang=${lang}?room=development`;
                              window.open(clientUrl, '_blank');
                            }}><ZoomIcon/> Join</a>
                          : '—'}
                      </td>
                      <td>
                        <button
                          className="hist-delete-btn"
                          onClick={() => deleteMeeting(m.id, m.name)}
                          disabled={deleting === m.id}
                          title="Delete meeting"
                        >
                          {deleting === m.id ? '…' : <DeleteIcon/>}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="hist-pagination">
              <button className="hist-page-btn" disabled={page===1} onClick={()=>setPage(p=>p-1)}>← Prev</button>
              {Array.from({length: Math.min(totalPages, 7)}, (_,i) => i+1).map(p => (
                <button key={p} className={`hist-page-btn${page===p?' active':''}`} onClick={()=>setPage(p)}>{p}</button>
              ))}
              <button className="hist-page-btn" disabled={page===totalPages} onClick={()=>setPage(p=>p+1)}>Next →</button>
            </div>
          )}

          {nextToken && !loading && (
            <div className="hist-load-more">
              <BtnSecondary onClick={()=>loadMeetings(emailInput, nextToken)}><RefreshIcon/> Load more from Zoom</BtnSecondary>
            </div>
          )}
        </PageCard>

        {/* ══════════════════════════════════════
            SERVER LOG MODAL
        ══════════════════════════════════════ */}
        {logOpen && (
          <div className="log-modal-backdrop" onClick={() => setLogOpen(false)}>
            <div className="log-modal" onClick={e => e.stopPropagation()}>
              <div className="log-modal-header">
                <span>Server Logs</span>
                <div style={{display:'flex', gap:8}}>
                  <button className="log-refresh-btn" onClick={fetchLogs} disabled={logLoading}>
                    <RefreshIcon/> {logLoading ? 'Loading…' : 'Refresh'}
                  </button>
                  <button className="log-close" onClick={() => setLogOpen(false)}>✕</button>
                </div>
              </div>
              <div className="log-body">
                {logLoading ? (
                  <div style={{padding:24}}>
                    {[1,2,3].map(i => <div key={i} style={{marginBottom:10}}><Skeleton height={14} radius={3}/></div>)}
                  </div>
                ) : logs.length === 0 ? (
                  <p className="log-empty">No log entries found.</p>
                ) : (
                  logs.slice(0, 300).map((l, i) => (
                    <div key={i} className={`log-line log-line--${l.level}`}>
                      {l.time && <span className="log-time">{l.time}</span>}
                      <span className="log-msg">{l.message}</span>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        )}

      </div>
    </MainLayout>
  );
};

export default History;
